import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { LineupCount as LineupCountIcon } from '@sorare/core/src/atoms/icons/LineupCount';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import Bold from '@sorare/core/src/atoms/typography/Bold';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { LineupCount } from '@sorare/core/src/components/lobby/CompetitionList/Ranking';
import { FormattedRank } from '@sorare/core/src/components/lobby/FormattedRank';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { fantasy } from '@sorare/core/src/lib/glossary';

const Wrapper = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding: 0;
`;

const LeftWrapper = styled(Horizontal).attrs({ gap: 0.5 })`
  & svg {
    display: block;
    font-size: 12px;
    color: var(--c-neutral-700);
  }
`;

const LineupCountWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  color: var(--c-neutral-700);
`;

type Props = {
  lineupsCount: number;
  locked: boolean;
  rank?: number | null;
  score?: number | null;
  joined?: boolean;
};

const LineupIconCount = ({ lineupsCount }: Pick<Props, 'lineupsCount'>) => (
  <>
    <LineupCountIcon />
    <LineupCountWrapper>
      <LineupCount count={lineupsCount} />
    </LineupCountWrapper>
  </>
);

const StyledScore = ({ score }: Pick<Props, 'score'>) => (
  <span>
    <FormattedMessage
      {...fantasy.pointsWithStyledScore}
      values={{ span: Bold, score }}
    />
  </span>
);

const Tags = ({ locked, joined }: Pick<Props, 'joined' | 'locked'>) => {
  const { formatMessage } = useIntlContext();
  return (
    <>
      {joined && (
        <Chip
          label={formatMessage({
            id: 'Lineup.Status.Joined',
            defaultMessage: 'Joined',
          })}
          custom={{
            color: 'white',
            background: 'var(--c-green-600)',
          }}
          size="smaller"
        />
      )}
      {locked && (
        <LabelM bold>
          {formatMessage({
            id: 'Lineup.Status.Locked',
            defaultMessage: 'Locked',
          })}
        </LabelM>
      )}
    </>
  );
};

export const LineupScoreRow = ({
  lineupsCount,
  locked,
  rank,
  score,
  joined,
}: Props) => {
  return (
    <Wrapper>
      <LeftWrapper>
        {rank ? (
          <FormattedRank rank={rank} total={lineupsCount} />
        ) : (
          <LineupIconCount lineupsCount={lineupsCount} />
        )}
      </LeftWrapper>
      {rank ? (
        <StyledScore score={score} />
      ) : (
        <Tags locked={locked} joined={joined} />
      )}
    </Wrapper>
  );
};
